/**
 * Ritual BGM — file-first (CC0 ambient) with loud, reliable Web Audio pad backup.
 * Progress is always clock-driven for act pacing.
 */
export class MusicDirector {
  private audio: HTMLAudioElement | null = null;
  private elapsed = 0;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private nodes: AudioNode[] = [];
  private started = false;
  private unlocked = false;
  readonly fallbackDurationSec: number;
  /** Last play error for UI debug (optional) */
  lastError: string | null = null;

  constructor(fallbackDurationSec = 210) {
    this.fallbackDurationSec = fallbackDurationSec;
  }

  async load(urls: string[] | string | null): Promise<void> {
    const list = !urls ? [] : Array.isArray(urls) ? urls : [urls];
    for (const candidate of list) {
      try {
        const a = new Audio();
        a.preload = "auto";
        a.loop = true;
        a.volume = 0.55;
        a.crossOrigin = "anonymous";
        a.src = candidate;
        await new Promise<void>((resolve, reject) => {
          const t = window.setTimeout(() => reject(new Error("timeout")), 12000);
          a.addEventListener(
            "canplaythrough",
            () => {
              window.clearTimeout(t);
              resolve();
            },
            { once: true },
          );
          a.addEventListener(
            "error",
            () => {
              window.clearTimeout(t);
              reject(new Error("error"));
            },
            { once: true },
          );
          a.load();
        });
        this.audio = a;
        return;
      } catch {
        /* try next */
      }
    }
  }

  /** Must run inside a user gesture (Begin click). */
  async unlock(): Promise<void> {
    try {
      const AC =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      if (!this.ctx) this.ctx = new AC();
      if (this.ctx.state === "suspended") await this.ctx.resume();
      // Silent buffer kick — unlocks iOS/Safari
      const buf = this.ctx.createBuffer(1, 1, 22050);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.ctx.destination);
      src.start(0);
      this.unlocked = true;
    } catch (e) {
      this.lastError = e instanceof Error ? e.message : "unlock failed";
    }
  }

  async play(): Promise<void> {
    this.elapsed = 0;
    this.lastError = null;
    await this.unlock();
    this.stopProc(false);
    this.startProc();

    if (this.audio) {
      try {
        this.audio.currentTime = 0;
        this.audio.volume = 0.6;
        const p = this.audio.play();
        if (p) await p;
      } catch (e) {
        this.lastError =
          e instanceof Error ? e.message : "html audio play blocked";
      }
    }
  }

  stop(): void {
    if (this.audio) {
      try {
        this.audio.pause();
        this.audio.currentTime = 0;
      } catch {
        /* */
      }
    }
    // Keep AudioContext alive across restarts — only stop graph nodes
    this.stopProc(false);
    this.elapsed = 0;
  }

  tick(dt: number): void {
    this.elapsed += dt;
    this.updateIntensity();
  }

  get progress(): number {
    return Math.min(1, this.elapsed / this.fallbackDurationSec);
  }

  get intensity(): number {
    const p = this.progress;
    if (p < 0.15) return 0.4;
    if (p < 0.35) return 0.6;
    if (p < 0.55) return 0.8;
    if (p < 0.8) return 0.95;
    return 1;
  }

  get isPlaying(): boolean {
    const file = !!this.audio && !this.audio.paused;
    const proc = this.started && !!this.ctx && this.ctx.state === "running";
    return file || proc;
  }

  private startProc(): void {
    if (!this.ctx || this.started) return;
    const ctx = this.ctx;
    const master = ctx.createGain();
    // linear start — exponentialRamp from ~0 is silent/broken in some browsers
    master.gain.value = 0.01;
    master.connect(ctx.destination);
    this.master = master;

    // Richer, louder pad so it's actually audible on phones
    this.addOsc(ctx, master, 48, "sine", 0.35);
    this.addOsc(ctx, master, 72, "triangle", 0.2);
    this.addOsc(ctx, master, 96, "sine", 0.12);
    this.addOsc(ctx, master, 144, "sawtooth", 0.04);
    this.addOsc(ctx, master, 192, "sine", 0.08);

    // Pulse “heartbeat” LFO on a mid tone
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.08;
    lfoGain.gain.value = 0.05;
    const pulse = ctx.createOscillator();
    const pulseGain = ctx.createGain();
    pulse.type = "sine";
    pulse.frequency.value = 110;
    pulseGain.gain.value = 0.08;
    lfo.connect(lfoGain);
    lfoGain.connect(pulseGain.gain);
    pulse.connect(pulseGain);
    pulseGain.connect(master);
    lfo.start();
    pulse.start();
    this.nodes.push(lfo, lfoGain, pulse, pulseGain);

    // Noise bed
    const noise = ctx.createBufferSource();
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.2;
    noise.buffer = buffer;
    noise.loop = true;
    const nf = ctx.createBiquadFilter();
    nf.type = "bandpass";
    nf.frequency.value = 300;
    nf.Q.value = 0.7;
    const ng = ctx.createGain();
    ng.gain.value = 0.07;
    noise.connect(nf);
    nf.connect(ng);
    ng.connect(master);
    noise.start();
    this.nodes.push(noise, nf, ng);

    const now = ctx.currentTime;
    master.gain.linearRampToValueAtTime(0.55, now + 0.8);
    this.started = true;
    this.updateIntensity();
  }

  private addOsc(
    ctx: AudioContext,
    dest: AudioNode,
    freq: number,
    type: OscillatorType,
    gainVal: number,
  ): void {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    const f = ctx.createBiquadFilter();
    osc.type = type;
    osc.frequency.value = freq;
    f.type = "lowpass";
    f.frequency.value = 800;
    g.gain.value = gainVal;
    osc.connect(f);
    f.connect(g);
    g.connect(dest);
    osc.start();
    this.nodes.push(osc, g, f);
  }

  private updateIntensity(): void {
    if (!this.master || !this.ctx) return;
    const i = this.intensity;
    // Louder overall; pad still carries if file fails
    const target = 0.4 + i * 0.35;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setTargetAtTime(target, now, 0.5);
    for (const n of this.nodes) {
      if (n instanceof BiquadFilterNode) {
        n.frequency.setTargetAtTime(400 + i * 1200, now, 0.6);
      }
    }
    if (this.audio) {
      this.audio.volume = 0.45 + i * 0.25;
    }
  }

  private stopProc(_close: boolean): void {
    for (const n of this.nodes) {
      try {
        if (n instanceof AudioBufferSourceNode || n instanceof OscillatorNode) {
          n.stop();
        }
        n.disconnect();
      } catch {
        /* */
      }
    }
    this.nodes = [];
    if (this.master) {
      try {
        this.master.disconnect();
      } catch {
        /* */
      }
    }
    this.master = null;
    this.started = false;
  }
}
